<template>
	<div class="lv-base-table"
		 :class="classes"
		 @mouseleave="lv_hover=null">
		<lv-table-column-controller @collect="lv_collect" ref="controller">
			<slot></slot>
		</lv-table-column-controller>
		<lv-table-head
			v-if="!noHeader"
			ref="head"
			:fixed-exist="lv_fixedExist"
			:head-columns="lv_headColumns"
			:body-columns="lv_bodyColumns"
			:sort-field="lv_sortField"
			:sort-desc="lv_sortDesc"
			:head-row-height="headRowHeight"
			:row-height="rowHeight"
			:fixed-row-data="p_fixedRowData"
			:column-filter="columnFilter"
			@mouseenter.native="lv_hover = 'head'"
			@mousewheel.native.catch="lv_headMousewheel"
			@scroll="e=>lv_hover === 'head' && $refs.body.$refs.center[0].$refs.scroll.setScroll({x: e.target.scrollLeft})"/>
		<lv-table-body
			ref="body"
			:table-data="p_tableData"
			:body-columns="lv_bodyColumns"
			:fixed-exist="lv_fixedExist"
			:row-height="rowHeight"
			:row-num="rowNum"
			:default-enable-editable="defaultEnableEditable"
			:edit-source-row="editSourceRow"
			:fast-render="fastRender"
			:disabled-body-scroll="disabledBodyScroll"
			@mouseenter.native="lv_hover = 'body'"
			@mousewheel.native.catch="lv_bodyMousewheel"
			@vertical-scroll-bottom="e=>$emit('vertical-scroll-bottom',e)"
			@scroll="e=>lv_hover !== 'head' && !!$refs.head && $refs.head.$refs.scroll.setScroll({x: e.target.scrollLeft})">
			<div class="lv-base-table-cover" v-show="(!data || data.length === 0) && showEmptyText">
				<div class="lv-base-table-cover-content" :style="{height:`${p_coverHeight}px`}">
					<link-icon icon="link-icon-zanwushuju"/>
					<span>{{$t('LinkAutoTable.noData')}}...</span>
				</div>
			</div>
		</lv-table-body>
	</div>
</template>

<script>
	import LvTableColumnController from './lv-table-column-controller';
	import {MountedMixin} from '../mixins';
	import {TableMixin} from './index';
	import LvTableHead from './lv-table-head';
	import LvTableBody from './lv-table-body';
	import {ColumnProps} from "../table-expansion/mixins";
	import {TableMark} from "./utils/TableMark";


	/**
	 * 表头行高只能通过css设置，.lv-table-head tr:not(.lv-table-column-space){}
	 * @author  韦胜健
	 * @date    2019/1/7 15:03
	 */
	export default {
		name: "lv-base-table",
		components: {LvTableBody, LvTableHead, LvTableColumnController},
		mixins: [MountedMixin, TableMixin],
		ColumnProps,
		props: {
			multiSelect: {type: Boolean},               //是否多选
			selectIndex: {},
			configColumnFunc: {type: Function},         //配置column的函数
			beforeConfigColumnFunc: {type: Function},   //在配置column之前触发的函数
			showEmptyText: {type: Boolean},             //是否展示无数据提示
			disabledSelect: {type: Boolean},			//是否禁用选中行动作
			treeConfig: {type: Object},					//树形表格所需要的配置信息对象
		},
		provide() {
			return {
				baseTable: this,
			}
		},
		data() {

			const mark = !!this.treeConfig ? new TableMark(() => this.treeConfig) : null

			return {
				columns: [],                            //所有列
				originalColumns: [],                    //所有列（原本的列）

				lv_hoverIndex: null,                    //鼠标浮动所在的行索引
				lv_selectedIndex: [],                   //选中的行索引数组
				lv_hostWidth: null,                     //表格显示宽度
				lv_hover: null,                         //鼠标是否覆盖在表格上
				lv_scrollLeft: false,                   //内容是否滑动到左端
				lv_scrollRight: false,                  //内容是否滑动到右端
				lv_sortField: this.sortField,           //排序字段
				lv_sortDesc: this.sortDesc,             //排序方式，先序降序
				p_tableWidth: null,                     //表格宽度
				p_tableDataCache: [],                   //p_tableData缓存数据
				content: {                              //各个表格的数据对象，rows：lv-table-row组件对象、timer排序定时器
					left: {rows: [], timer: null},
					center: {rows: [], timer: null},
					right: {rows: [], timer: null},
				},
				mark,
			}
		},
		watch: {
			/**
			 * 监听selectIndex的变化，设置选中的行
			 * @author  韦胜健
			 * @date    2019/1/12 10:19
			 */
			selectIndex(val) {
				this.$nextTick(() => this.selectRow(val))
				this.lv_selectedIndex = val
			},
			lv_selectedIndex(val) {
				this.$emit('update:selectIndex', val)
			},
			sortField(val) {
				this.lv_sortField = val
			},
			sortDesc(val) {
				this.lv_sortDesc = val
			},
			data(newVal, oldVal) {
				if (newVal !== oldVal) {
					this.$refs.body.scrollTop(0)
				}
			},
		},
		computed: {
			classes() {
				return {
					'lv-base-table-hover': !!this.lv_hover,
					'lv-base-table-left-shadow': !this.lv_scrollLeft,
					'lv-base-table-right-shadow': !this.lv_scrollRight,
				}
			},
			/*
			 *  判断左右表格是否应该存在
			 *  @author     martsforever
			 *  @datetime   2019/1/6 21:52
			 */
			lv_fixedExist() {
				const ret = {left: false, center: false, right: false}
				this.columns.forEach(col => Object.keys(ret).forEach(position => !ret[position] && (ret[position] = col.fixed === position)))
				return ret
			},
			/*
			 *  表头渲染列信息数组
			 *  @author     martsforever
			 *  @datetime   2019/1/6 21:51
			 */
			lv_headColumns() {
				if (!this.lv_mounted) return []
				let maxLevel = 1;
				let columns = this.p_copyColumns(this.columns)

				/*计算最大层数*/
				const calculateLavel = (cols, level) => {
					if (!!cols && cols.length > 0) {
						if (level > maxLevel) maxLevel = level
						cols.forEach((col) => {
							col.level = level - 1
							!!col.group && calculateLavel(col.children, level + 1)
						})
					}
				}
				/*计算多级表头每个单元格所占行数以及列数*/
				const calculateSpan = (col) => {
					if (!!col.group) {
						col.children.forEach(item => calculateSpan(item))
						col.rowspan = 1
						col.colspan = 0
						col.children.forEach(item => col.colspan += item.colspan)
					} else {
						col.rowspan = maxLevel - col.level
						col.colspan = 1
					}
				}
				calculateLavel(columns, 1)
				columns.forEach(i => calculateSpan(i))
				const headColumns = []
				let i = 0
				while (i < maxLevel) {
					headColumns.push([])
					i++
				}
				/*收集多级表头渲染数据*/
				const calculateHeadColumns = (cols) => {
					if (!!cols && cols.length > 0) {
						cols.forEach((col) => {
							headColumns[col.level].push(col)
							!!col.group && calculateHeadColumns(col.children)
						})
					}
				}
				calculateHeadColumns(columns)
				return headColumns
			},
			/*
			 *  表体渲染列信息数组
			 *  @author     martsforever
			 *  @datetime   2019/1/6 21:51
			 */
			lv_bodyColumns() {
				if (!this.lv_mounted) return []
				const itar = (columns, ret) => {
					columns.forEach(item => {
						if (!!item.group) {
							itar(item.children, ret)
						} else {
							ret.push(item)
						}
					})
				}
				const cols = []
				/*收集渲染的列*/
				itar(this.columns, cols)
				/*首次加载还没有挂载mounted的时候，columns应该是空的，收集的bodyColumns也是空的，直接返回空数组*/
				if (cols.length === 0) return cols
				/*计算所有列的总宽度*/
				const totalColumnWidth = cols.reduce((ret, item) => ret + item.width, 0)
				/*如果所有列的总宽度小于表格宽度，按照列的权重给列分配剩下的宽度*/
				if (totalColumnWidth < this.p_tableWidth) {
					/*额外多出来的宽度*/
					let externalWidth = this.p_tableWidth - totalColumnWidth - 1
					/*总权重*/
					let totalColumnFit = cols.reduce((ret, item) => ret + item.fit, 0)
					/*如果列中没有配置权重，则最后一个非固定列设置权重为1*/
					if (totalColumnFit === 0) {
						for (let i = cols.length - 1; i >= 0; i--) {
							const col = cols[i]
							if (col.fixed !== 'left' && col.fixed !== 'right') {
								col.fit = 1
								break
							}
						}
						totalColumnFit = 1
					}
					/*一份权重所增加的宽度*/
					let externalChunkWidth = Math.floor(externalWidth / totalColumnFit) - 1
					cols.forEach(col => col.width = col.width + Math.floor(col.fit * externalChunkWidth))
				}
				return cols
			},
			/**
			 * 表格数据
			 * @author  韦胜健
			 * @date    2019/3/21 19:14
			 */
			p_tableData() {
				if (!this.mark) {
					const ret = (this.data || []).map(item => {
						const cacheItem = this.$lv.$utils.findOne(this.p_tableDataCache, (ci) => ci.row === item)
						if (!!cacheItem) return cacheItem
						else return {
							id: this.$lv.$utils.uuid(),
							row: item,
							editRow: this.$lv.$utils.deepCopy(item),
						}
					})
					this.p_tableDataCache = [...ret]
					return ret
				} else {
					return this.p_treeNodeData.flatTreeData
				}
			},
			p_treeNodeData() {
				const list = this.mark.node.getList(this.data, 1, () => null, false)
				return {
					treeData: list,
					flatTreeData: this.mark.node.flatListData(list).formatDataFlat,
				}
			},
			/**
			 * 固定行数据
			 * @author  韦胜健
			 * @date    2019/7/2 11:52
			 */
			p_fixedRowData() {
				if (!this.fixedRowData || this.fixedRowData.length === 0) return null
				return this.fixedRowData.map(item => ({
					id: this.$lv.$utils.uuid(),
					row: item,
					editRow: this.$lv.$utils.deepCopy(item),
					fixedRow: true,
				}))
			},
			/**
			 * 遮罩的高度
			 * @author  韦胜健
			 * @date    2020/1/8 10:41
			 */
			p_coverHeight() {
				let num = this.rowNum > 10 ? 10 : this.rowNum
				return this.rowHeight * num
			},
		},
		created() {
			this.$on('rowEnter', ({row, rowIndex, position}) => this.lv_rowIterate(row => row.lv_hover = true, rowIndex))     	//监听行鼠标覆盖行事件
			this.$on('rowLeave', ({row, rowIndex, position}) => this.lv_rowIterate(row => row.lv_hover = false, rowIndex))      //监听行鼠标覆盖行事件
			// this.$on('rowDblClick',({row,rowIndex,position})=>this.lv_rowDblClick({row,rowIndex,position}))                  //监听行鼠标双击行事件
			this.$on('rowClick', this.lv_click)                                                                                 //监听行鼠标单击行事件
			this.$on('scrollLeft', (val) => this.lv_scrollLeft = val)                                                           //内容滑动到左端
			this.$on('scrollRight', (val) => this.lv_scrollRight = val)                                                         //内容滑动到右端
			// this.$on('clickTitle', this.lv_clickTitle)                                                                          //点击标题动作
		},
		async mounted() {
			this.lv_calculateWidth()
			window.addEventListener('resize', this.lv_calculateWidth)
		},
		beforeDestroy() {
			window.removeEventListener('resize', this.lv_calculateWidth)
		},
		methods: {
			/**
			 * 开启编辑状态
			 * @author  韦胜健
			 * @date    2019/1/8 10:49
			 */
			enableEdit(rowIndex) {
				this.lv_rowIterate(row => row.enableEdit(), rowIndex)
			},
			/**
			 * 关闭编辑状态
			 * @author  韦胜健
			 * @date    2019/1/8 10:28
			 */
			disableEdit(rowIndex) {
				this.lv_rowIterate(row => row.disableEdit(), rowIndex)
			},
			/**
			 * 开始编辑
			 * @author  韦胜健
			 * @date    2019/1/8 10:28
			 */
			startEdit(rowIndex) {
				if (rowIndex != null && rowIndex !== '') {
					const {row, editRow} = this.p_tableData[rowIndex]
					Object.keys(row).forEach(key => editRow[key] = row[key])
				} else {
					this.p_tableData.forEach(item => {
						const {row, editRow} = item
						Object.keys(row).forEach(key => editRow[key] = row[key])
					})
				}
				this.enableEdit(rowIndex)
			},
			/**
			 * 取消编辑，并且将editRow复原
			 * @author  韦胜健
			 * @date    2019/1/8 10:28
			 */
			cancelEdit(rowIndex) {
				if (rowIndex != null && rowIndex !== '') {
					const {row, editRow} = this.p_tableData[rowIndex]
					Array.from([...Object.keys(row), ...Object.keys(editRow)]).forEach(key => editRow[key] = row[key])
				} else {
					this.p_tableData.forEach(item => {
						const {row, editRow} = item
						Array.from([...Object.keys(row), ...Object.keys(editRow)]).forEach(key => editRow[key] = row[key])
					})
				}
				this.disableEdit(rowIndex)
			},
			/**
			 * 获取编辑的数据
			 * @author  韦胜健
			 * @date    2019/1/9 10:41
			 */
			getEdit() {
				return this.content.center.rows.reduce((ret, item) => {
					!!item.lv_editing && ret.push({row: item.row, editRow: item.editRow})
					return ret
				}, [])
			},
			/**
			 * 保存编辑的数据
			 * @author  韦胜健
			 * @date    2019/1/9 10:41
			 */
			saveEdit(results) {
				if (!results || results.length < 1) {
					console.error('results 不应该为空！')
					return
				}
				results.forEach(({row, editRow, newRow}) => {
					Object.keys(newRow).forEach(key => this.$set(editRow, key, newRow[key]))
					Object.keys(editRow).forEach(key => this.$set(row, key, editRow[key]))
				})
			},
			/**
			 * 刷新列
			 * @author  韦胜健
			 * @date    2019/1/9 16:15
			 */
			refreshRender() {
				this.$refs.controller.collect()
			},
			/**
			 * 选中行
			 * @author  韦胜健
			 * @date    2019/1/11 16:37
			 */
			selectRow(rowIndex) {
				this.lv_rowIterate((row, index) => row.lv_selected = rowIndex === index)
			},
			/**
			 * 校验数据
			 * @author  韦胜健
			 * @date    2019/1/14 16:54
			 */
			validate() {
				let ret = {flag: true, validateMsg: null, field: null, col: null}
				this.lv_rowIterate((row) => {
					if (!row.lv_editing || !ret.flag) return
					const {flag, validateMsg, field, col} = row.validate()
					if (!flag) ret = {flag, validateMsg, field, col}
				})
				return ret
			},
			/*
			 *  计算宿主宽度，左右表格的显示宽度应该分别是左右列宽之和，但是里面的head、body的宽度应该为宿主宽度
			 *  @author     martsforever
			 *  @datetime   2019/1/6 23:09
			 */
			lv_calculateWidth() {
				this.lv_hostWidth = this.$el.offsetWidth
			},
			/*
			 *  收集列数据信息
			 *  @author     martsforever
			 *  @datetime   2019/1/6 20:44
			 */
			async lv_collect(columns) {
				/*等待属性变化完成*/
				await this.$lv.nextTick()
				/*保存原本的列信息*/
				this.originalColumns = this.p_copyColumns(columns)
				!!this.beforeConfigColumnFunc && (await this.beforeConfigColumnFunc(columns, this.p_colIterate))
				/*配置列*/
				this.p_colIterate(columns, (col, isGroup, cols) => {
					if (col.disabledConfig) return
					!!this.configColumnFunc && this.configColumnFunc(col, isGroup, cols)
					if (isGroup) return
					if (col.hide) {
						cols.splice(cols.indexOf(col), 1)
						return 1
					}
				})

				/*---------------------------------------排序-------------------------------------------*/
				function orderFunc(a, b) {
					const aOrder = a.order + (a.fixed === 'left' ? 999 : a.fixed === 'right' ? -999 : 0)
					const bOrder = b.order + (b.fixed === 'left' ? 999 : b.fixed === 'right' ? -999 : 0)
					return aOrder < bOrder
				}

				/*递归遍历子节点，如果是多级表头，则对子列进行插入排序*/
				this.p_colIterate(columns, (col, isGroup) => {
					if (isGroup && !!col.children && col.children.length > 0) {
						col.children.forEach(item => item.fixed = col.fixed)
						this.$lv.$utils.insertSort(col.children, orderFunc)
					}
				})
				/*对最外层列或者列组进行插入排序*/
				this.$lv.$utils.insertSort(columns, orderFunc);

				this.columns = columns

				await this.$lv.nextTick()
				await this.$lv.nextTick()
				this.$emit('collect', this.lv_bodyColumns)
				/**
				 * FormOption 在 closeEdit的时候可能会触发页面back，此时表格中的作用域模板或者列可能会通过 FormOption中的部分变量来控制，
				 * 导致触发表格内容的 mutation observer事件，这里做判断，没有就不往下操作
				 * @author  韦胜健
				 * @date    2020/2/12 16:48
				 */
				if (!!this.$refs.body && this.$refs.body.$el) {
					this.p_tableWidth = this.$refs.body.$el.offsetWidth
				}
			},
			/**
			 * 复制columns
			 * @author  韦胜健
			 * @date    2019/2/20 16:13
			 */
			p_copyColumns(columns) {
				const ret = []
				if (!columns || columns.length === 0) return ret
				for (let i = 0; i < columns.length; i++) {
					const col = columns[i];
					if (col.group) col.children = this.p_copyColumns(col.children)
					ret.push(col)
				}
				return ret
			},
			/**
			 * 递归遍历所有列以及列组
			 * @author  韦胜健
			 * @date    2019/2/20 09:45
			 */
			p_colIterate(columns, fn) {
				if (!columns || columns.length === 0) return
				else {
					for (let i = 0; i < columns.length; i++) {
						const col = columns[i];
						const handleRet = fn(col, !!col.group, columns)
						i -= (handleRet != null && this.$lv.$utils.typeOf(handleRet) === 'number' ? handleRet : 0)
						if (!!col.group) this.p_colIterate(col.children, fn)
					}
				}
			},
			/*
			 *  触发Row组件对象的方法
			 *  @author     martsforever
			 *  @datetime   2019/1/6 20:55
			 */
			lv_rowIterate(fn, index) {
				Object.keys(this.content).forEach(position => {
					if (this.content[position].rows.length > 0) {
						if (index != null && index !== '') {
							if (!!this.content[position].rows[index]) {
								fn(this.content[position].rows[index], index)
							} else {
								console.error(`row[${position} -- ${index}] is not exist!`)
							}
						} else {
							this.content[position].rows.forEach((row, i) => {
								fn(row, i)
							})
						}
					}
				})
			},
			/*
			 *  收集行组件对象
			 *  @author     martsforever
			 *  @datetime   2019/1/6 20:46
			 */
			lv_rowAdd({row, position}) {
				this.content[position].rows.push(row)
				if (!!this.content[position].timer) {
					clearTimeout(this.content[position].timer)
					this.content[position].timer = null
				}
				/*如果rows的长度小于data的长度，表明还有row没有初始化完成，等待10毫秒再对row进行排序*/
				this.content[position].rows.length < this.data.length ? (this.content[position].timer = setTimeout(() => this.lv_sortRows(position), 10)) : this.lv_sortRows(position)
			},
			/*
			 *  删除行组件对象
			 *  @author     martsforever
			 *  @datetime   2019/1/6 20:46
			 */
			lv_rowRemove({row, position}) {
				!!this.content[position] && this.$lv.$utils.removeFromArray(this.content[position].rows, row)
			},
			/*
			 *  处理行鼠标滑过事件
			 *  @author     martsforever
			 *  @datetime   2019/1/6 21:00
			 */
			lv_rowHover({row, rowIndex, position}) {
				this.lv_hoverIndex != null && (this.lv_rowIterate((row) => row.lv_hover = false, this.lv_hoverIndex))
				this.lv_rowIterate((row) => row.lv_hover = true, rowIndex)
				this.lv_hoverIndex = rowIndex
			},
			/**
			 * 根據data中的顺序，对content中的row component进行排序
			 * @author  韦胜健
			 * @date    2019/1/9 10:32
			 */
			lv_sortRows(position) {
				this.content[position].rows.sort((a, b) => a.rowIndex - b.rowIndex)
			},
			/**
			 * 处理点击标题事件
			 * @author  韦胜健
			 * @date    2019/1/9 14:39
			 */
			lv_clickTitle({col}) {
				/*派发排序事件*/
				if (!col.sort) return
				if (!col.children) {
					if (this.lv_sortField == null) {
						this.lv_sortField = col.field
						this.lv_sortDesc = true
					} else {
						if (this.lv_sortField === col.field) {
							if (col.field === this.lv_sortField && !this.lv_sortDesc) {
								this.lv_sortField = this.sortField
								this.lv_sortDesc = col.field === this.sortField ? !this.lv_sortDesc : this.sortDesc
							} else {
								this.lv_sortDesc = false
							}
						} else {
							this.lv_sortField = col.field
							this.lv_sortDesc = true
						}
					}
					this.$emit('sortChange', {field: this.lv_sortField, desc: this.lv_sortDesc})
				}
			},
			/**
			 * 单击行事件
			 * @author  韦胜健
			 * @date    2019/1/9 18:05
			 */
			lv_click({row, rowIndex}) {
				if (!!this.disabledSelect) return
				this.lv_selectedIndex = rowIndex
				if (!this.multiSelect) this.lv_rowIterate(row => row.unselect())
				this.lv_rowIterate(row => row.select(), this.lv_selectedIndex != null ? this.lv_selectedIndex : rowIndex)
			},
			/**
			 * 监听表头鼠标滚轮事件
			 * @author  韦胜健
			 * @date    2019/11/6 9:18
			 */
			lv_headMousewheel(e) {
				if (e.deltaX === 0) {
					e.stopPropagation()
					e.preventDefault()
					const startX = this.$refs.body.$refs.center[0].$refs.scroll.contentWrapperScrollLeft
					this.$refs.body.$refs.center[0].$refs.scroll.setScroll({x: startX + e.deltaY})
					this.$refs.head.$refs.scroll.setScroll({x: startX + e.deltaY})
				}
			},
			/**
			 * 监听表体鼠标滚轮事件
			 * @author  韦胜健
			 * @date    2019/11/6 11:32
			 */
			lv_bodyMousewheel(e) {
				if (!!e.altKey) {
					e.stopPropagation()
					e.preventDefault()
					const startX = this.$refs.body.$refs.center[0].$refs.scroll.contentWrapperScrollLeft
					this.$refs.body.$refs.center[0].$refs.scroll.setScroll({x: startX + e.deltaY})
					this.$refs.head.$refs.scroll.setScroll({x: startX + e.deltaY})
				}
			},
		}
	}
</script>

<style lang="scss">
	.lv-base-table {
		@include public-style;
		width: 100%;
		position: relative;
		/*border: solid 1px $base-table-border-color;*/
		box-sizing: border-box;

		table {
			border-collapse: collapse;
		}

		.lv-base-table-cover {
			position: absolute;
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			z-index: 1;
			pointer-events: none;
			overflow: hidden;

			.lv-base-table-cover-content {
				display: flex;
				align-items: center;
				justify-content: center;
				flex-direction: column;
				font-size: 12px;
				color: rgba(0, 0, 0, 0.65);

				.link-icon {
					font-size: 80px;
				}

				& > span {
					margin-top: 1em;
				}
			}
		}

		* {
			box-sizing: border-box;
		}

		.lv-table-content-left, .lv-table-content-right {
			position: absolute;
			top: 0;
		}

		.lv-table-content-right {
			right: 0;
		}

		.lv-scroll-horizontal-indicator {
			z-index: 1;
		}

		&.lv-base-table-hover {
			.lv-scroll-horizontal-indicator, .lv-scroll-vertical-indicator {
				opacity: 1 !important;
			}
		}

		.lv-table-body-item, .lv-table-head-item {
			@include transition-all;
			box-shadow: 0 0 10px transparent;
		}

		&.lv-base-table-left-shadow {
			.lv-table-body-item-left, .lv-table-head-item-left {
				box-shadow: 0 0 10px rgba(black, 0.12);
			}
		}

		&.lv-base-table-right-shadow {
			.lv-table-body-item-right, .lv-table-head-item-right {
				box-shadow: 0 0 10px rgba(black, 0.12);
			}
		}
	}
</style>
